<script lang="ts">
	import "@glidejs/glide/dist/css/glide.core.min.css";
	import logo from "../../../core/assets/logo.svg";
	import type { UploadedFile } from "../../../core/schema/UploadedFile.js";
	import type { Product } from "../../../core/schema/Product.js";
	import DynamicPicture from "./Picture/DynamicPicture.svelte";
	import StaticPicture from "./Picture/StaticPicture.svelte";
	import { appName } from "../../../core/schema/names/appName.js";
	import type { Image } from "../../../core/schema/Image.js";
	import translations from "./ProductGallery.translations.json";
	import GalleryThumbnails from "./GalleryThumbnails.svelte";
	import DynamicVideo from "./video/DynamicVideo.svelte";
	import { isFileVideo } from "../../../core/utils/isFileVideo.js";
	import { getTranslate } from "../utils/getTranslate.js";

	export let media: UploadedFile[];
	export let product: Product;
	let containerClass = "";
	export { containerClass as class };

	const bigMediaWidth = 600;
	const smallMediaWidth = 300;

	$: [primaryMedia, ...otherMedia] = media;

	let displayedMedia: UploadedFile | undefined;

	function displayImage(media: Image): void {
		displayedMedia = media;
	}
	const translate = getTranslate(translations);
</script>

<div class="relative {containerClass}">
	{#if primaryMedia}
		{@const mediaToDisplay = displayedMedia ?? primaryMedia}
		{#if isFileVideo(mediaToDisplay)}
			<DynamicVideo
				class="border-lttertiary aspect-[4/3] w-full border bg-white"
				videoClass="size-full object-cover"
				video={mediaToDisplay}
				width={bigMediaWidth}
				height={Math.round((bigMediaWidth * 3) / 4)}
			/>
		{:else}
			<DynamicPicture
				class="border-lttertiary aspect-[4/3] w-full border bg-white"
				imgClass="size-full object-cover"
				image={mediaToDisplay}
				lightboxWidth={1000}
				width={bigMediaWidth}
				height={Math.round((bigMediaWidth * 3) / 4)}
				bucket="storage"
				loading="eager"
				fetchpriority="high"
				preload
				alt="{translate('photo')} – {product.name} (1/{media.length})"
				sourcesets={{
					"<md": [490, 368],
					">md": [900, 675],
				}}
			/>
		{/if}
	{:else}
		<div class="bg-lttertiary-100 flex aspect-[4/3] w-full items-center justify-center">
			<StaticPicture
				alt="Logo {appName}"
				image={logo}
				class="w-28 opacity-50 lg:w-36 xl:w-40"
				loading="eager"
				width={160}
				height={160}
			/>
		</div>
	{/if}
	{#if otherMedia.length > 0}
		<GalleryThumbnails ariaPrevious={translate("previous")} ariaNext={translate("next")}>
			{#each otherMedia as other, i}
				{@const mediaToDisplay = displayedMedia === other && primaryMedia ? primaryMedia : other}
				<button
					on:click={() => {
						displayImage(mediaToDisplay);
					}}
					class="border-lttertiary aspect-[4/3] w-1/4 shrink-0 snap-start overflow-hidden border bg-white p-0 [&:not(:first-child)]:-ml-px"
				>
					{#if isFileVideo(mediaToDisplay)}
						<DynamicVideo
							class="size-full transition-transform duration-300 hover:scale-105"
							videoClass="size-full object-cover"
							autoplay={false}
							video={mediaToDisplay}
							width={smallMediaWidth}
							height={Math.round((smallMediaWidth * 3) / 4)}
							icon={true}
						/>
					{:else}
						<DynamicPicture
							class="size-full transition-transform duration-300 hover:scale-105"
							imgClass="size-full object-cover"
							image={mediaToDisplay}
							width={smallMediaWidth}
							height={Math.round((smallMediaWidth * 3) / 4)}
							bucket="storage"
							loading="lazy"
							alt="{translate('photo')} – {product.name} ({i + 2}/{media.length})"
						/>
					{/if}
				</button>
			{/each}
		</GalleryThumbnails>
	{/if}
</div>
